import React, { useState, useEffect, useRef, useCallback } from 'react';

import { PureTranslator, Translator } from 'ggApp/modules/translator';
import useIntersection from 'ggApp/shared/hooks/useIntersection';

import { CURRENCIES_ASSOCIATION } from 'ggApp/modules/store/constants';
import { formatBundleDetails } from './formatBundleDetails';

import {
    Wrap,
    Header,
    ProductGrid,
    MainProduct,
    BundleProductsWrap,
    BundleProduct,
    PlusSeperator,
    PlusIcon,
    PriceWrap,
    OldPrice,
    CurrentPrice,
    PlanDescription,
    Button,
} from './styled';

const BundleWidget = ({
    className,
    isDesktopUser,
    selectedRentalPlan,
    activeStore,
    handleBundleCtaClick,
    mainProduct,
    secondaryProducts,
    currentVariant,
    handleIntersect,
    registerAddBundleToCart,
    bundleID,
}) => {
    const bundleRef = useRef(null);

    const bundleContent =
        mainProduct && secondaryProducts?.length
            ? formatBundleDetails({
                  productList: [mainProduct, ...secondaryProducts],
                  planDuration: selectedRentalPlan?.minimumMonths,
                  language: activeStore.activeLanguage,
                  currency: CURRENCIES_ASSOCIATION[activeStore.default_currency],
                  currentVariant,
              })
            : null;

    useIntersection({
        node: bundleRef,
        onEnterCallback: () => handleIntersect(bundleContent),
    });

    const registerAddBundleToCartEvent = useCallback(
        (orderID) => {
            registerAddBundleToCart({
                bundleContent,
                bundleID,
                orderID,
            });
        },
        [bundleContent, bundleID, registerAddBundleToCart],
    );

    const mainBundleProduct = bundleContent?.products?.[0];
    const secondaryBundleProducts = bundleContent?.products?.slice(1);
    const secondaryProductsQty = secondaryBundleProducts?.length;

    return (
        <Wrap className={className} ref={bundleRef}>
            <Header id="bundleHeader">
                <PureTranslator tk="BUNDLE_TITLE" />
            </Header>
            {bundleContent?.products?.length && (
                <ProductGrid>
                    <MainProduct
                        activeStore={activeStore}
                        product={mainBundleProduct}
                        cardType={!isDesktopUser ? 'horizontal' : 'vertical'}
                    />
                    <PlusSeperator bundleProductsQty={secondaryProductsQty}>
                        <PlusIcon size="32" />
                    </PlusSeperator>

                    <BundleProductsWrap bundleProductsQty={secondaryProductsQty}>
                        {secondaryBundleProducts.map((product, index) => {
                            return (
                                <React.Fragment key={product.slug}>
                                    <BundleProduct
                                        activeStore={activeStore}
                                        product={product}
                                        cardType={
                                            !isDesktopUser || secondaryBundleProducts.length > 1
                                                ? 'horizontal'
                                                : 'vertical'
                                        }
                                    />
                                    {index !== secondaryProductsQty - 1 && !isDesktopUser && (
                                        <PlusSeperator
                                            bundleProductsQty={secondaryProductsQty}
                                            withMargin
                                        >
                                            <PlusIcon size="32" />
                                        </PlusSeperator>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </BundleProductsWrap>
                </ProductGrid>
            )}
            {bundleContent && (
                <PriceWrap>
                    {bundleContent.totalOldBundlePrice && (
                        <OldPrice>{bundleContent.totalOldBundlePrice}</OldPrice>
                    )}
                    <CurrentPrice withOldPrice={bundleContent.totalOldBundlePrice}>
                        {bundleContent.totalBundlePrice}
                    </CurrentPrice>
                    <PlanDescription>
                        <PureTranslator
                            tk={
                                selectedRentalPlan.minimumMonths > 1
                                    ? 'BUNDLES_PLAN_DURATION'
                                    : 'BUNDLES_PLAN_DURATION_SHORT'
                            }
                            withValues={{ duration: selectedRentalPlan.minimumMonths }}
                        />
                    </PlanDescription>
                </PriceWrap>
            )}
            <Button
                id="bundleCta"
                onClick={() =>
                    handleBundleCtaClick({ bundleContent, callback: registerAddBundleToCartEvent })
                }
            >
                <PureTranslator tk="BUNDLES_CTA_TEXT" />
            </Button>
        </Wrap>
    );
};

export default BundleWidget;
